import api, { getAuthHeaders } from './_client';

export const avatarApi = {
  /**
   * Save (upsert) the current user's avatar config.
   * Returns { avatarId, updatedAt } on success, null on failure.
   */
  saveMyAvatar: async (config, userInfo = null) => {
    try {
      let sessionId;
      try {
        const { useSessionStore } = require('../store');
        const store = useSessionStore.getState();
        sessionId = store.currentSessionId || store.getOrCreateSessionId();
      } catch (_) {
        sessionId = null;
      }

      const authHeaders = await getAuthHeaders(userInfo);
      const response = await api.put('/avatars/me', { config }, {
        headers: {
          ...authHeaders,
          ...(sessionId && { 'x-session-id': sessionId })
        }
      });

      return response.data?.data || response.data;
    } catch (error) {
      console.warn('Failed to save avatar to backend:', error.message);
      return null;
    }
  },

  /**
   * Get the current user's avatar from the backend.
   * Returns { avatarId, config, ... } or null.
   */
  getMyAvatar: async (userInfo = null) => {
    try {
      let sessionId;
      try {
        const { useSessionStore } = require('../store');
        const store = useSessionStore.getState();
        sessionId = store.currentSessionId || store.getOrCreateSessionId();
      } catch (_) {
        sessionId = null;
      }

      const authHeaders = await getAuthHeaders(userInfo);
      const response = await api.get('/avatars/me', {
        headers: {
          ...authHeaders,
          ...(sessionId && { 'x-session-id': sessionId })
        }
      });

      return response.data?.data || response.data;
    } catch (error) {
      if (error.response?.status === 404) return null;
      console.warn('Failed to get avatar from backend:', error.message);
      return null;
    }
  },

  /**
   * Get a user's avatar by their userId (public, no auth needed).
   * Used by creator profile pages to render the owner's avatar.
   */
  getAvatarByUserId: async (userId) => {
    try {
      const response = await api.get(`/avatars/by-user/${encodeURIComponent(userId)}`);
      return response.data?.data || response.data;
    } catch (error) {
      if (error.response?.status === 404) return null;
      console.warn(`Failed to get avatar for user ${userId}:`, error.message);
      return null;
    }
  },

  /**
   * Get a single avatar by ID (public, no auth needed).
   */
  getAvatarById: async (avatarId) => {
    try {
      const response = await api.get(`/avatars/${avatarId}`);
      return response.data?.data || response.data;
    } catch (error) {
      if (error.response?.status === 404) return null;
      console.warn(`Failed to get avatar ${avatarId}:`, error.message);
      return null;
    }
  },

  /**
   * Batch fetch avatar configs by IDs (public, no auth needed).
   * Returns { avatars: { [avatarId]: { config } } }
   */
  getAvatarsByIds: async (avatarIds) => {
    if (!avatarIds || avatarIds.length === 0) return {};
    try {
      const response = await api.post('/avatars/batch', { avatarIds });
      const data = response.data?.data || response.data;
      return data?.avatars || {};
    } catch (error) {
      console.warn('Failed to batch fetch avatars:', error.message);
      return {};
    }
  },

  /**
   * Migrate guest avatar to authenticated user.
   */
  migrateGuestAvatar: async (userInfo = null) => {
    try {
      let sessionId;
      try {
        const { useSessionStore } = require('../store');
        const store = useSessionStore.getState();
        sessionId = store.currentSessionId || store.getOrCreateSessionId();
      } catch (_) {
        sessionId = null;
      }

      const authHeaders = await getAuthHeaders(userInfo);
      const response = await api.post('/avatars/migrate', {}, {
        headers: {
          ...authHeaders,
          ...(sessionId && { 'x-session-id': sessionId })
        }
      });

      return response.data?.data || response.data;
    } catch (error) {
      console.warn('Failed to migrate guest avatar:', error.message);
      return null;
    }
  }
};
